import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
// import { toast } from "react-toastify";
import { useToaster } from "../_components/toaster/MuiToasterProvider";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { SaveLoader } from "@/_components";
import { accountService } from "@/_services";
import { history } from "@/_helpers";
import { green } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { styledSlots, resolveStyledSlots } from "../_helpers/styledSlots";
import LineImage from "@/_images/orLine.png";
import LineImageEs from "@/_images/oLine.png";
const GreenRadio = styled((props) => <Radio color="default" {...props} />)(
  ({ theme }) =>
    styledSlots(
      resolveStyledSlots(
        {
          root: {
            color: green[400],
            "&$checked": {
              color: green[600],
            },
          },
          checked: {},
        },
        theme,
      ),
    ),
);
const RedTextTypography = styled(Typography)(({ theme }) =>
  styledSlots(
    resolveStyledSlots(
      {
        root: {
          fontSize: 12,
          color: "#d1495b",
          fontWeight: 600,
          fontStyle: "italic",
          textAlign: "center",
        },
      },
      theme,
    ),
  ),
);
const componentStyles = (theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  buttonIco: {
    width: 24,
    height: 24,
    marginRight: 10,
    marginTop: 7,
  },
  inputIco: {
    opacity: 0.6,
    color: "#1c355e",
  },
});
const StyledRoot = styled("div")(({ theme }) => ({
  display: "contents",
  ...Object.entries(componentStyles(theme)).reduce(
    (acc, [className, style]) => ({
      ...acc,
      [`& .${className}, &.${className}`]: style,
    }),
    {},
  ),
}));
function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}
function ForgotPassword() {
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [tempInfo, setTempInfo] = useState();
  const [showResetOptions, setShowResetOptions] = useState(false);
  const [showResetCode, setShowResetCode] = useState(false);
  const [resetOption, setResetOption] = useState("");
  const [invalidCodeCtr, setInvalidCodeCtr] = useState(1);
  const [isPhone, setIsPhone] = useState(false);
  const [inputValue, setInputValue] = useState("");
  // get email from query parameters and populate it as default value
  const email = useQuery().get("email");
  const toast = useToaster();
  const {
    register,
    handleSubmit,
    control,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      email,
    },
  });
  const handleInputChange = (event) => {
    let { value } = event.target;
    const hasAt = value.includes("@");
    const hasLetter = /[a-zA-Z]/.test(value || "");
    let isPhoneValue = false;
    if (hasAt || hasLetter) {
      setIsPhone(false);
      isPhoneValue = false;
    } else {
      setIsPhone(true);
      isPhoneValue = true;
    }
    if (countryCode === "USA" && isPhoneValue) {
      const numericValue = value.replace(/[^\d]/g, "");
      if (numericValue.length > 3 && numericValue.length <= 6) {
        value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
      } else if (numericValue.length > 6) {
        value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
      }
    } else {
      value = value.replace(/[^a-zA-Z\d@.+_-]/g, "");
    }
    setInputValue(value);
  };
  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      const isUSCountry = countryCode === "USA" || countryCode === "US";
      const submittedIdentifier = data?.email || tempInfo?.email || "";
      const resetIdentifier = isPhone ? null : submittedIdentifier;
      const phoneNumber = isPhone
        ? submittedIdentifier.replace(/\D/g, "")
        : null;
      const resetRequest = {
        emailAddress: resetIdentifier || "",
        phoneNumber: phoneNumber || "",
        isPhone,
      };

      if (showResetOptions === false && showResetCode === false) {
        const options = await accountService.forgotPassword(
          resetIdentifier,
          isPhone,
          phoneNumber,
        );
        const mechanisms = options?.mechanisms || [];
        let resetSMS = mechanisms.includes("SMS");
        const resetEmail = mechanisms.includes("Email");

        if (!isUSCountry) {
          resetSMS = false;
        }

        setTempInfo({
          email: resetIdentifier || "",
          emailAddress: resetIdentifier || "",
          phoneNumber: phoneNumber || "",
          isPhone,
          userId: options?.userId || options?.userID,
        });

        if (resetSMS && resetEmail) {
          setShowResetOptions(true);
        } else if (resetSMS) {
          await accountService.sendResetCode(resetRequest, "Lifesafer", "SMS");
          toast.success(t("account.resetCodeSent"));
          setResetOption("SMS");
          setShowResetCode(true);
        } else if (resetEmail) {
          await accountService.sendResetCode(
            resetRequest,
            "Lifesafer",
            "Email",
          );
          toast.success(t("account.resetPasswordSuccess"));
          setResetOption("Email");
          setShowResetCode(true);
        } else {
          toast.error(t("account.resetPasswordError"));
        }
      } else if (showResetOptions === true && showResetCode === false) {
        if (!resetOption) {
          toast.error(t("account.resetPasswordError"));
          return;
        }

        await accountService.sendResetCode(tempInfo, "Lifesafer", resetOption);
        toast.success(
          resetOption === "SMS"
            ? t("account.resetCodeSent")
            : t("account.resetPasswordSuccess"),
        );
        setShowResetOptions(false);
        setShowResetCode(true);
      } else if (showResetCode === true) {
        if (invalidCodeCtr >= 3) {
          toast.error(t("account.resetCodeAttempts"));
          history.push("/account/login");
          return;
        }

        const code = parseInt(data.resetCode, 10);
        const rtn = await accountService.validateResetCode(
          tempInfo.userId,
          code,
        );

        if (rtn.reason === "Reset code verification failure") {
          setInvalidCodeCtr(invalidCodeCtr + 1);
          toast.error(t("account.resetCodeInvalid"));
        } else {
          const resetLoginIdentifier = tempInfo?.isPhone
            ? tempInfo?.phoneNumber || phoneNumber || submittedIdentifier
            : tempInfo?.emailAddress ||
              tempInfo?.email ||
              submittedIdentifier ||
              email ||
              "";
          const resetSearch = new URLSearchParams();
          resetSearch.set("token", data.resetCode);
          resetSearch.set("userId", tempInfo.userId);
          resetSearch.set("email", resetLoginIdentifier);
          resetSearch.set("username", resetLoginIdentifier);
          resetSearch.set("isPhone", tempInfo?.isPhone ? "true" : "false");

          history.push({
            pathname: "/account/reset-password",
            search: `?${resetSearch.toString()}`,
          });
        }
      }
    } catch (error) {
      if (showResetCode === true) {
        toast.error(t("account.resetCodeError"));
      } else if (isPhone) {
        toast.error(t("account.resetPhoneError"));
      } else {
        toast.error(t("account.resetPasswordError"));
      }
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };
  const handleRadioChange = (event) => {
    let selectedOption = event.target.value;
    setResetOption(selectedOption);
    //TODO FINISH THIS
  };
  return (
    <StyledRoot>
      <>
        <form className={"form"} onSubmit={handleSubmit(onSubmit)}>
          {showResetCode === true ? (
            <Grid
              container
              style={{
                paddingTop: 20,
                paddingBottom: 50,
              }}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                size="small"
                id="resetCode"
                label={t("account.resetCode")}
                name="resetCode"
                autoComplete="resetCode"
                error={!!errors.resetCode}
                helperText={errors.resetCode ? errors.resetCode.message : ""}
                autoFocus
                slotProps={{
                  htmlInput: {
                    ...register("resetCode", {
                      required: {
                        value: true,
                        message: t("account.resetCodeRequired"),
                      },
                      pattern: {
                        value: /^[0-9]*$/,
                        message: t("account.resetCodeInputInvalid"),
                      },
                    }),
                  },
                }}
              />
            </Grid>
          ) : showResetOptions === true ? (
            <Grid
              container
              style={{
                paddingTop: 20,
                paddingBottom: 50,
              }}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Grid
                container
                // size={{
                //   xs: 6,
                // }}
                sx={{
                  flexDirection: "column",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                {(countryCode === "USA" || countryCode === "US") && (
                  <>
                    <Grid
                      container
                      style={
                        {
                          // paddingLeft: 50,
                        }
                      }
                      sx={{
                        alignItems: "center",
                        flexDirection: "row",
                      }}
                    >
                      <Grid>
                        <GreenRadio
                          id="resetOptionText"
                          name="resetOptionText"
                          checked={resetOption === "SMS"}
                          onChange={handleRadioChange}
                          value={"SMS"}
                        />
                      </Grid>
                      <Grid>
                        <Typography>{t("account.receiveText")}</Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      style={{
                        // paddingLeft: 50,
                        paddingBottom: 20,
                      }}
                      sx={{
                        justifyContent: "center",
                        flexDirection: "row",
                      }}
                    >
                      <Grid>
                        <RedTextTypography>
                          {t("account.providerChgMessage")}
                        </RedTextTypography>
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid
                  container
                  style={
                    {
                      // paddingLeft: 50,
                    }
                  }
                  sx={{
                    alignItems: "center",
                    flexDirection: "row",
                  }}
                >
                  <Grid>
                    <GreenRadio
                      id="resetOptionEmail"
                      name="resetOptionEmail"
                      checked={resetOption === "Email"}
                      onChange={handleRadioChange}
                      value={"Email"}
                    />
                  </Grid>
                  <Grid>
                    <Typography>{t("account.receiveEmail")}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            // <TextField
            //   variant="outlined"
            //   margin="normal"
            //   fullWidth
            //   id="email"
            //   label={t("account.email")}
            //   name="email"
            //   autoComplete="email"
            //   error={!!errors.email}
            //   helperText={errors.email ? errors.email.message : ""}
            //   autoFocus
            //   inputRef={register({
            //     required: { value: true, message: t("account.emailRequired") },
            //     pattern: {
            //       value: /^\S+@\S+$/i,
            //       message: t("account.emailValid"),
            //     },
            //   })}
            //   InputProps={{
            //     startAdornment: (
            //       <InputAdornment position="start">
            //         <EmailOutlinedIcon className={"inputIco"} />
            //       </InputAdornment>
            //     ),
            //   }}
            // />

            <Controller
              name="email"
              control={control}
              defaultValue={inputValue}
              rules={{
                required: {
                  value: true,
                  message:
                    countryCode === "USA"
                      ? t("account.phoneEmailRequired")
                      : t("account.emailRequired"),
                },
                validate: (value) => {
                  if (countryCode === "USA" && !value) {
                    return t("account.phoneEmailRequired");
                  }
                  const cleanValue = value.trim();
                  const hasAt = cleanValue.includes("@");
                  const hasLetter = /[a-zA-Z]/.test(cleanValue || "");
                  if (countryCode === "AU" || hasAt || hasLetter) {
                    const isValidEmail = /^\S+@\S+\.\S+$/.test(cleanValue);
                    return isValidEmail ? true : t("account.emailValid");
                  } else {
                    if (
                      value.replace(/\D/g, "").length === 10 ||
                      value.replace(/\D/g, "").length === 11 ||
                      value.length === 0
                    ) {
                      return true;
                    } else {
                      return t("account.phoneValid");
                    }
                  }
                },
              }}
              render={({ field: __fieldField }) => {
                const field = {
                  ...__fieldField,
                  value: __fieldField.value ?? null,
                };
                return (
                  <TextField
                    {...field}
                    data-cy="email"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    size="small"
                    id="email"
                    label={
                      countryCode === "USA"
                        ? t("account.phoneEmail")
                        : t("account.email")
                    }
                    name="email"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email ? errors.email.message : ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      field?.onChange(e);
                      trigger("email");
                    }}
                    value={inputValue}
                    maxLength={10}
                    autoFocus
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon className={"inputIco"} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                );
              }}
            />
          )}
          <Grid
            container
            sx={{
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Grid>
              {submitting === false ? (
                <Button
                  type="submit"
                  size="medium"
                  disabled={submitting}
                  variant="contained"
                  color="primary"
                  className={"submit"}
                  style={{
                    width: 240,
                    height: 50,
                  }}
                >
                  {t("account.resetPasswordButton")}
                </Button>
              ) : (
                <SaveLoader />
              )}
            </Grid>
            <Grid>
              {i18n?.language === "es" ? (
                <img src={LineImageEs} alt="O" />
              ) : (
                <img src={LineImage} alt="OR" />
              )}
            </Grid>
            <Grid
              style={{
                paddingBottom: 15,
              }}
            >
              <RouterLink
                to="login"
                style={{
                  textDecoration: "none",
                }}
              >
                <Button
                  type="submit"
                  size="medium"
                  disabled={submitting}
                  variant="contained"
                  color="secondary"
                  style={{
                    width: 240,
                    height: 50,
                  }}
                >
                  {t("account.backToSignInButton")}
                </Button>
              </RouterLink>
            </Grid>
          </Grid>
        </form>
      </>
    </StyledRoot>
  );
}
ForgotPassword.propTypes = {
  history: PropTypes.object.isRequired,
};
export default ForgotPassword;
